import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { formatFirestoreTimestampMMDDYYYY } from '../../../utils/time_utils/TimeUtils';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../resources/strings/StringKeys';

const styles = StyleSheet.create({
  page: {
    padding: 50,
    fontFamily: 'Helvetica',
  },
  header: {
    marginBottom: 30,
    borderBottom: '1px solid #666',
    paddingBottom: 10,
  },
  logo: {
    width: 120,
    marginBottom: 10,
  },
  title: {
    fontSize: 24,
    marginBottom: 10,
    color: '#1976d2', // Primary blue color
  },
  subtitle: {
    fontSize: 14,
    color: '#666',
    marginBottom: 5,
  },
  section: {
    marginTop: 25,
    marginBottom: 10,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10,
    backgroundColor: '#f5f5f5',
    padding: 8,
    color: '#1976d2',
  },
  question: {
    marginBottom: 15,
  },
  questionText: {
    fontSize: 12,
    marginBottom: 5,
    fontWeight: 'bold',
  },
  response: {
    marginLeft: 15,
    fontSize: 11,
    marginBottom: 5,
  },
  rating: {
    color: '#1976d2',
    fontSize: 11,
    fontWeight: 'bold',
  },
  comments: {
    fontSize: 11,
    marginLeft: 15,
    fontStyle: 'italic',
    color: '#666',
  },
  signatures: {
    marginTop: 30,
    borderTop: '1px solid #666',
    paddingTop: 20,
  },
  signatureRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 15,
  },
  signatureBlock: {
    width: '45%',
  },
  signatureLabel: {
    fontSize: 10,
    color: '#666',
    marginBottom: 5,
  },
  signatureDate: {
    fontSize: 10,
    color: '#666',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 50,
    right: 50,
    textAlign: 'center',
    fontSize: 10,
    color: '#666',
  },
});

const PerformanceReviewPDF = ({ review, employee, manager, organizationLogo }) => {

const { t } = useTranslation()

  const getRatingLabel = (rating) => {
    const ratings = {
      greatly_exceeds: 'Greatly Exceeds Expectations',
      exceeds: 'Exceeds Expectations',
      meets: 'Meets Expectations',
      below: 'Below Expectations',
      far_below: 'Far Below Expectations'
    };
    return ratings[rating] || rating;
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          {organizationLogo && (
            <Image style={styles.logo} src={organizationLogo} />
          )}
          <Text style={styles.title}>{t(StringKeys.PERFORMANCE_REVIEW)}</Text>
          <Text style={styles.subtitle}>{t(StringKeys.EMPLOYEE)}: {employee?.first_name} {employee?.last_name}</Text>
          <Text style={styles.subtitle}>{t(StringKeys.REVIEW_CYCLE)}: {review.review_cycle}</Text>
          <Text style={styles.subtitle}>{t(StringKeys.DUE_DATE)}: {formatFirestoreTimestampMMDDYYYY(review.due_date)}</Text>
        </View>

        {/* Review Sections */}
        {['performance', 'goals', 'feedback'].map((section) => (
          <View key={section} style={styles.section}>
            <Text style={styles.sectionTitle}>
              {section.charAt(0).toUpperCase() + section.slice(1)} {t(StringKeys.SECTION)}
            </Text>
            {review.questions
              .filter(q => q.section === section)
              .map((question, index) => (
                <View key={index} style={styles.question}>
                  <Text style={styles.questionText}>{question.text}</Text>
                  
                  {/* Employee Response */}
                  <View style={styles.response}>
                    <Text style={styles.rating}>
                      {t(StringKeys.EMPLOYEE_RESPONSE)}: {
                        question.type === 'multipleChoice' 
                          ? getRatingLabel(question.responses.employee.answer)
                          : question.responses.employee.answer
                      }
                    </Text>
                    {question.responses.employee.comments && (
                      <Text style={styles.comments}>
                        {t(StringKeys.COMMENTS)}: {question.responses.employee.comments}
                      </Text>
                    )}
                  </View>

                  {/* Manager Response */}
                  <View style={styles.response}>
                    <Text style={styles.rating}>
                      {t(StringKeys.MANAGER_RESPONSE)}: {
                        question.type === 'multipleChoice' 
                          ? getRatingLabel(question.responses.manager.answer)
                          : question.responses.manager.answer
                      }
                    </Text>
                    {question.responses.manager.comments && (
                      <Text style={styles.comments}>
                        {t(StringKeys.COMMENTS)}: {question.responses.manager.comments}
                      </Text>
                    )}
                  </View>
                </View>
              ))}
          </View>
        ))}

        {/* Signatures Section */}
        <View style={styles.signatures}>
          <View style={styles.signatureRow}>
            <View style={styles.signatureBlock}>
              <Text style={styles.signatureLabel}>{t(StringKeys.EMPLOYEE_ACKNOWLEDGEMENT)}</Text>
              <Text style={styles.signatureDate}>
                {t(StringKeys.DATE)}: {review.acknowledgments?.employee_acknowledged 
                  ? formatFirestoreTimestampMMDDYYYY(review.updated_at)
                  : t(StringKeys.PENDING)}
              </Text>
            </View>
            <View style={styles.signatureBlock}>
              <Text style={styles.signatureLabel}>{t(StringKeys.MANAGER_ACKNOWLEDGEMENT)}</Text>
              <Text style={styles.signatureDate}>
                {t(StringKeys.DATE)}: {review.acknowledgments?.manager_acknowledged 
                  ? formatFirestoreTimestampMMDDYYYY(review.updated_at)
                  : t(StringKeys.PENDING)}
              </Text>
            </View>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text>{new Date().toLocaleDateString()}</Text>
        </View>
      </Page>
    </Document>
  );
};

export default PerformanceReviewPDF; 